/**
 * Turn on Logins, the stepper on Settings › Security (piece 21; decision
 * 1840): four steps that take an installation from the break-glass login alone
 * to every employee signing in through the identity provider. Each step's done
 * test is read from rows the plan already names. 🔒 Nothing here is stored.
 */
export const LOGIN_STEPS_PATH = '/auth/logins/setup-steps';

/** Where the stepper stands, and the pages its steps link to. */
export const TURN_ON_LOGINS_ROUTE = '/settings/security';
export const LOGINS_ROUTE = '/settings/people/logins';
export const SINGLE_SIGN_ON_ROUTE = '/settings/security/single-sign-on';
export const RECOVERY_COPY_ROUTE = '/settings/documents/storage';

export const LOGIN_STEPS = ['wayBack', 'provider', 'everyLogin', 'enforced'] as const;
export type LoginStep = (typeof LOGIN_STEPS)[number];

/** What the server reads; every field is a read of a saved row or the server's own mode, and none is stored for the stepper. */
export interface LoginStepsView {
  /** The break-glass login no longer carries the shipped default password (`UserRecord.mustChangePassword` false). */
  breakGlassPasswordSet: boolean;
  /** The break-glass login holds a live `MfaEnrollment` row. */
  breakGlassFactor: boolean;
  /** The master key's recovery copy is sealed under a passphrase (decision 1195). */
  recoveryCopySealed: boolean;
  /** The provider's issuer, client id and sealed secret are all saved. */
  providerConfigured: boolean;
  /** `auth.ssoEnabled` is on. */
  ssoEnabled: boolean;
  /** `auth.ssoPasswordOverlap` is on, so internal passwords still sign in beside the provider. */
  passwordOverlap: boolean;
  /** Successful sign-ins through the provider, from `LoginAttempt` rows. */
  ssoSignIns: number;
  /** Open employments at the Organization's own company. */
  employees: number;
  /** Those of them with no `UserRecord` at all. */
  withoutLogin: number;
  /** The server's enforcement mode is `enforced`. */
  enforced: boolean;
}

export interface LoginStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 1840). The way back is held when the
 * break-glass login carries its own password and a second factor and the
 * recovery copy is sealed. The provider is configured and tested when its
 * three rows are saved, the switch is on and one sign-in through it has
 * succeeded. Every employee has a login when no open employment at our own
 * company lacks one. Enforcement is on when the server enforces, the switch is
 * on and the password overlap is off, after the provider's test. A key the
 * answer lacks reads as not done.
 */
export function loginSteps(view: Partial<LoginStepsView>): Record<LoginStep, LoginStepFacts> {
  const wayBack = view.breakGlassPasswordSet === true && view.breakGlassFactor === true && view.recoveryCopySealed === true;
  const provider = view.providerConfigured === true && view.ssoEnabled === true && (view.ssoSignIns ?? 0) > 0;
  const everyLogin = (view.employees ?? 0) > 0 && view.withoutLogin === 0;
  const enforced = provider && view.enforced === true && view.passwordOverlap === false;
  return {
    wayBack: { done: wayBack, reachable: true },
    provider: { done: provider, reachable: true },
    everyLogin: { done: everyLogin, reachable: true },
    enforced: { done: enforced, reachable: enforced || (wayBack && provider && everyLogin) },
  };
}
